import { useCallback } from 'react';
import { notification } from 'components';
import { ErrorMappingValueType, MicroserviceTypes } from 'typings';
import { useAttachmentDeleteMutation, useAttachmentFileDeleteMutation } from 'api';

export const useDeleteModal = (onDeleteModalClose: () => void, entityName: string, service?: MicroserviceTypes) => {
  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const [attachmentDelete, { isLoading: attachmentDeleteIsLoading }] = useAttachmentDeleteMutation();
  const [attachmentFileDelete, { isLoading: fileDeleteIsLoading }] = useAttachmentFileDeleteMutation();
  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onRemoveConfirm = useCallback(
    async (attachment: Record<string, any> | undefined) => {
      if (attachment) {
        try {
          const response = await attachmentDelete({
            entityName,
            id: attachment.id,
            service,
          });
          if (response?.data?.success) {
            if (response.data.data?.inherited) {
              openNotificationWithIcon({
                type: 'success',
                message: 'Attachment was successfully deleted.',
              });
              onDeleteModalClose();
              return;
            }
            const fileResponse = await attachmentFileDelete({
              id: attachment.id,
              name: attachment.fileName,
            });

            // TODO what if the error occurs during file deletion
            if (fileResponse?.data?.success) {
              openNotificationWithIcon({
                type: 'success',
                message: 'Attachment was successfully deleted.',
              });
              onDeleteModalClose();
            } else {
              handleErrors(fileResponse.error);
            }
          } else {
            handleErrors(response.error);
          }
        } catch (err) {
          handleErrors(err);
        }
      }
    },
    [
      attachmentDelete,
      openNotificationWithIcon,
      onDeleteModalClose,
      handleErrors,
      attachmentFileDelete,
      service,
      entityName,
    ]
  );

  return {
    onRemoveConfirm,
    contextHolder,
    isLoading: fileDeleteIsLoading || attachmentDeleteIsLoading,
  };
};
